import { FormControl, InputLabel, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type {
  MUIDataTableColumnOptions,
  MUIDataTableOptions,
} from 'mui-datatables';
import React from 'react';

import { DateInput } from '@components/DateInput';

import { isValidDate } from '@utils/date';
import { formatFhirDate } from '@utils/fhir';
import { toISO } from '@utils/format';
import { sortDates } from '@utils/sort/date';

import CustomFooter from './CustomFooter';

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;

export const defaultTableOptions: MUIDataTableOptions = {
  selectableRows: 'none',
  viewColumns: false,
  download: false,
  print: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [],
  caseSensitive: false,
  searchPlaceholder: 'Search items...',
  search: false,
  filter: false,
  sort: false,
  customFooter: (
    rowCount,
    page,
    rowsPerPage,
    changeRowsPerPage,
    changePage
  ) => {
    return (
      <CustomFooter
        changePage={changePage}
        changeRowsPerPage={changeRowsPerPage}
        page={page}
        rowCount={rowCount}
        rowsPerPage={rowsPerPage}
      />
    );
  },
};

export const defaultColumnOptions: MUIDataTableColumnOptions = {
  searchable: false,
  sort: false,
  filter: false,
};

export const dateColumnOptions: MUIDataTableColumnOptions = {
  filterType: 'custom',
  sortCompare: sortDates,
  customFilterListOptions: {
    render: (v) => {
      const [low, high] = v.map((date: any) =>
        formatFhirDate(date, {
          friendly: true,
          adjustTz: false,
          showTime: false,
        })
      );
      if (low && high) return `${low} – ${high}`;
      if (low) return `From: ${low}`;
      if (high) return `Up to: ${high}`;
      return '';
    },
    update: (filterList, filterPos, index) => {
      if (filterPos === 0) {
        filterList[index].splice(filterPos, 1, '');
      } else if (filterPos === 1) {
        filterList[index].splice(filterPos, 1);
      } else if (filterPos === -1) {
        filterList[index] = [];
      }
      return filterList;
    },
  },
  filterOptions: {
    logic(date, filters) {
      // Returning `true` removes the item from the data
      if (!isValidDate(new Date(date)) && filters.length) {
        return true;
      }
      const afterStart = filters[0] ? dayjs(date).isBefore(filters[0]) : false;
      const beforeEnd = filters[1] ? dayjs(date).isAfter(filters[1]) : false;
      return afterStart || beforeEnd;
    },
    display: (filterList, onChange, index, column) => {
      const { label } = column;
      const filterValues = [...filterList[index]];
      const [low, high] = filterValues.map((value) =>
        value
          ? dayjs(value).add(dayjs(value).utcOffset(), 'minutes').toISOString()
          : value
      );
      return (
        <div>
          <Typography
            sx={{
              paddingBottom: '11px',
              fontSize: '14px',
            }}
          >
            {label} Range
          </Typography>
          <FormControl>
            <InputLabel
              htmlFor={`filter-${column.name}-start`}
              sx={{
                position: 'relative',
                paddingBottom: '8px',
                fontSize: '12px',
                marginLeft: '-11px',
              }}
            >
              From
            </InputLabel>
            <div id={`filter-${column.name}-start`}>
              <DateInput
                onChange={(value) => {
                  // @ts-expect-error onChange is mistyped to not allow nulls
                  filterValues[0] = toISO({
                    date: value,
                    to: 'start',
                    of: 'day',
                    utc: true,
                  });
                  onChange(filterValues, index, column);
                }}
                value={low}
              />
            </div>
          </FormControl>
          <FormControl sx={{ mt: 0.5, mb: 1 }}>
            <InputLabel
              htmlFor={`filter-${column.name}-end`}
              sx={{
                position: 'relative',
                paddingBottom: '8px',
                fontSize: '12px',
                marginLeft: '-11px',
              }}
            >
              To
            </InputLabel>
            <div id={`filter-${column.name}-end`}>
              <DateInput
                onChange={(value) => {
                  // @ts-expect-error onChange is mistyped to not allow nulls
                  filterValues[1] = toISO({
                    date: value,
                    to: 'end',
                    of: 'day',
                    utc: true,
                  });
                  onChange(filterValues, index, column);
                }}
                value={high}
              />
            </div>
          </FormControl>
        </div>
      );
    },
  },
};
